import { ActionIcon, Box, Group, Loader, Stack, Text } from "@mantine/core";
import { useMemo, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useTranslation } from "react-i18next";

import Center from "~/components/Center";
import Dialog from "~/components/Dialog";
import type { MeqColumnDef, MeqRowData } from "~/components/MeqTable/types";
import SvgIconRaw from "~/components/SvgIconRaw";
import { useOrgs } from "~/hooks";
import useCameraImageSrc from "~/hooks/Camera/useCameraImageSrc";
import useDatasetTagging from "~/hooks/Camera/useDatasetTagging";
import type { CameraColumnValueTypeV2 } from "~/hooks/Camera/v2";
import { getDateFormat } from "~/utils/Helper";
import { LooseObject } from "~/utils/Types";
import { formatDateValue, toUnitSymbol, withUnitSuffix } from "~/utils/columns";
import dayjs from "~/utils/dayjs";
import { formatToMaxDecimals } from "~/utils/formatToMaxDecimals";

import ImageDetailsTraits from "../ImageDetailsTraits";
import NoImagePlaceholder from "../NoImagePlaceholder";
import { resolveImageIdentity } from "./imageIdentity";

/**
 * Fixed viewer geometry. Prev/next swaps one carcase for another inside a modal
 * that must not resize as it goes, so neither dimension may follow the content:
 * the keyframe is contained inside a fixed box and the trait list scrolls. The
 * height cap matches `Dialog`'s own, so the modal never scrolls as a whole.
 */
const VIEWER_WIDTH = 768;
const VIEWER_HEIGHT = "min(960px, calc(100dvh - 4rem))";
const DETAILS_WIDTH = 300;

const PLANT_USER_GRADE_ORG = "jbswagyu_omaha";

type TraitMeta = { unitsName?: string; valueType?: CameraColumnValueTypeV2 };

const getTraitMeta = (trait: MeqColumnDef<MeqRowData>): TraitMeta =>
  (trait.meta as TraitMeta | undefined) ?? {};

const formatTraitValue = (
  value: unknown,
  noValue: string,
  { dateFormat, dateTimeFormat, valueType }: TraitMeta & DateFormats,
) => {
  if (value === undefined || value === null || value === "") return noValue;
  if (valueType === "date" || valueType === "datetime") {
    const format = valueType === "date" ? dateFormat : dateTimeFormat;
    return formatDateValue(value, format) ?? noValue;
  }
  if (typeof value === "boolean") return String(value);
  return String(formatToMaxDecimals(value));
};

type DateFormats = { dateFormat: string; dateTimeFormat: string };

/** Default for `useItemDetail` when the caller has nothing to fetch. */
const EMPTY_ITEM_DETAIL = {};
const useNoopItemDetail = (): { data?: LooseObject; isLoading?: boolean } =>
  EMPTY_ITEM_DETAIL;

export type ImageViewerProps = {
  bodyIdKey: string;
  columns: MeqColumnDef<MeqRowData>[];
  dateKey: string;
  /** See `ImagesModalProps.detailColumns`. */
  detailColumns?: MeqColumnDef<MeqRowData>[];
  idKey: string;
  imgUrlKey: string;
  /** The row the caller clicked. Seeds the position within `rows`. */
  initialItem: LooseObject;
  keyframeHashKey: string;
  locationKey: string;
  onClose: () => void;
  /** Ordered set prev/next walks — the caller's resolved view, not all rows. */
  rows: LooseObject[];
  /** See `ImagesModalProps.useItemDetail`. */
  useItemDetail?: (item: LooseObject) => {
    data?: LooseObject;
    isLoading?: boolean;
  };
};

/**
 * One camera image at full size, with its traits beside it.
 *
 * The keyframe is `contain`ed rather than cropped: a grader is reading the eye
 * muscle and the fat depth off it, so nothing may be cut. Aspect ratios vary
 * carcase to carcase, which is what the tinted box behind it absorbs.
 */
const ImageViewer = ({
  bodyIdKey,
  columns,
  dateKey,
  detailColumns = [],
  idKey,
  imgUrlKey,
  initialItem,
  keyframeHashKey,
  locationKey,
  onClose,
  rows,
  useItemDetail,
}: ImageViewerProps) => {
  const { t } = useTranslation("camera");
  const { currentOrg } = useOrgs();
  const dateFormat = getDateFormat(currentOrg, "short");
  const dateTimeFormat = getDateFormat(currentOrg, "default");

  const [index, setIndex] = useState(() => {
    const seeded = rows.findIndex((row) => row[idKey] === initialItem[idKey]);
    return seeded < 0 ? 0 : seeded;
  });

  // `rows` can be replaced under the viewer when the query behind it settles;
  // falling back keeps a valid item rather than blanking the modal.
  const currentItem = rows[index] ?? initialItem;
  const total = rows.length;

  const hasPrevious = index > 0;
  const hasNext = index < total - 1;
  const goPrevious = () => hasPrevious && setIndex(index - 1);
  const goNext = () => hasNext && setIndex(index + 1);

  useHotkeys("ArrowLeft", goPrevious, [index, total]);
  useHotkeys("ArrowRight", goNext, [index, total]);

  const { src, isLoading, hasError, onError, retry } = useCameraImageSrc({
    item: currentItem,
    imgUrlKey,
    isThumbnail: false,
    keyframeHashKey,
    locationKey,
  });

  const datasetTagging = useDatasetTagging(currentItem);

  // Re-invoked every render with the current item, so it naturally re-fires
  // on open and on every prev/next navigation — same as useCameraImageSrc/
  // useDatasetTagging above reacting to currentItem. Always one hook call
  // here regardless of whether the caller supplied one (rules-of-hooks).
  const itemDetail = (useItemDetail ?? useNoopItemDetail)(currentItem);
  // Only adds keys currentItem doesn't have — never masks real row data.
  const traitItem = useMemo(
    () => ({ ...currentItem, ...itemDetail.data }),
    [currentItem, itemDetail.data],
  );

  // No client-side reordering: the v2 column set arrives in the order its
  // preset defines, and re-sorting it here would silently outrank the preset.
  // The two keys the header already renders are dropped structurally rather
  // than by a caller-supplied list, so a preset that leads with barcode cannot
  // show it twice. detailColumns are appended after — they're never in the
  // header (that only ever comes from `columns`, via idKey/dateKey).
  const traits = useMemo(() => {
    const inHeader = new Set([idKey, dateKey]);

    return [...columns, ...detailColumns].filter(
      (column) =>
        typeof column.accessorKey === "string" &&
        !inHeader.has(column.accessorKey),
    );
  }, [columns, dateKey, detailColumns, idKey]);

  const showPlantUserGrade = useMemo(
    () =>
      currentOrg?.idString === PLANT_USER_GRADE_ORG &&
      traits.some((trait) =>
        String(trait.accessorKey).includes("JBS_WAGYU_MARBLING"),
      ),
    [currentOrg, traits],
  );

  const { barcode, bodyId } = resolveImageIdentity({
    bodyIdKey,
    idKey,
    item: currentItem,
  });
  const title =
    bodyId === undefined
      ? barcode
      : t("imagesModal.tileBody", { value: bodyId });
  const captured = dayjs(currentItem[dateKey]);

  return (
    <Dialog open onClose={onClose} maxWidth={false}>
      <Stack
        gap={0}
        style={{
          borderRadius: "var(--mantine-radius-sm)",
          height: VIEWER_HEIGHT,
          maxWidth: "100%",
          overflow: "hidden",
          width: VIEWER_WIDTH,
        }}
      >
        <Group
          align="flex-start"
          justify="space-between"
          gap={16}
          px={24}
          py={12}
          wrap="nowrap"
          style={{
            borderBottom: "1px solid var(--mantine-color-border-secondary)",
            flex: "0 0 auto",
          }}
        >
          <Stack gap={2} style={{ minWidth: 0 }}>
            {/* Three nodes rather than one string: the barcode truncates from
                the front (whole plants share a prefix), and Mantine implements
                that as `direction: rtl`, which would throw the separator to
                the wrong end of a combined line. */}
            <Group gap={8} wrap="nowrap" style={{ minWidth: 0 }}>
              <Text
                size="lg"
                fw={600}
                truncate="start"
                style={{ flex: bodyId === undefined ? "0 1 auto" : "0 0 auto" }}
              >
                {title}
              </Text>
              {bodyId !== undefined && barcode !== "" && (
                <>
                  <Text
                    size="lg"
                    c="var(--mantine-color-text-secondary)"
                    style={{ flex: "0 0 auto" }}
                  >
                    ·
                  </Text>
                  <Text
                    size="lg"
                    c="var(--mantine-color-text-secondary)"
                    truncate="start"
                    style={{ minWidth: 0 }}
                  >
                    {barcode}
                  </Text>
                </>
              )}
            </Group>
            <Text size="sm" c="var(--mantine-color-text-secondary)">
              {captured.isValid()
                ? captured.format("DD MMM YYYY HH:mm:ss")
                : ""}
            </Text>
          </Stack>

          <Group gap={8} wrap="nowrap" style={{ flex: "0 0 auto" }}>
            <Text
              size="sm"
              c="var(--mantine-color-text-secondary)"
              aria-label={t("imagesModal.imagePosition", {
                current: index + 1,
                total,
              })}
            >
              {`${index + 1} / ${total}`}
            </Text>
            <ActionIcon
              aria-label={t("imagesModal.previousImage")}
              disabled={!hasPrevious}
              onClick={goPrevious}
              size="input-sm"
              variant="secondary"
            >
              <SvgIconRaw name="ChevronLeft" size={18} />
            </ActionIcon>
            <ActionIcon
              aria-label={t("imagesModal.nextImage")}
              disabled={!hasNext}
              onClick={goNext}
              size="input-sm"
              variant="secondary"
            >
              <SvgIconRaw name="ChevronRight" size={18} />
            </ActionIcon>
            <ActionIcon
              aria-label={t("imagesModal.close")}
              onClick={onClose}
              size="input-sm"
              variant="secondary"
            >
              <SvgIconRaw name="XClose" size={18} />
            </ActionIcon>
          </Group>
        </Group>

        <Box
          style={{
            display: "grid",
            flex: 1,
            gridTemplateColumns: `minmax(0, 1fr) ${DETAILS_WIDTH}px`,
            minHeight: 0,
          }}
        >
          {/* Tertiary, not the usual secondary page tint: this box is what a
              varying aspect ratio letterboxes against, so it has to read as a
              box against the white panel beside it rather than blend in. */}
          <Center
            style={{
              backgroundColor: "var(--mantine-color-bg-tertiary)",
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            {src && !hasError ? (
              <img
                onDragStart={(event) => event.preventDefault()}
                src={src}
                alt={title}
                onError={onError}
                style={{
                  height: "100%",
                  objectFit: "contain",
                  width: "100%",
                }}
              />
            ) : isLoading ? (
              <Loader />
            ) : (
              <NoImagePlaceholder onRefresh={retry} />
            )}
          </Center>

          <Box p={16} style={{ minWidth: 0, overflowY: "auto" }}>
            {datasetTagging.isEnabled ? (
              <ImageDetailsTraits
                traitsInterested={traits}
                overrides={datasetTagging.overrides}
                tagsResult={datasetTagging.tagsResult}
                currentItem={currentItem}
                tags={datasetTagging.tags}
                showPlantUserGrade={showPlantUserGrade}
              />
            ) : (
              <>
                <Text
                  size="xs"
                  fw={600}
                  tt="uppercase"
                  c="var(--mantine-color-text-secondary)"
                  mb={4}
                >
                  {t("imagesModal.imageDetails")}
                </Text>
                {traits.map((trait) => (
                  <Group
                    key={String(trait.accessorKey)}
                    align="flex-start"
                    justify="space-between"
                    gap={8}
                    py={8}
                    wrap="nowrap"
                    style={{
                      borderBottom:
                        "1px solid var(--mantine-color-border-secondary)",
                    }}
                  >
                    <Text size="sm" c="var(--mantine-color-text-secondary)">
                      {withUnitSuffix(
                        String(trait.header),
                        toUnitSymbol(
                          traitItem[getTraitMeta(trait).unitsName ?? ""],
                        ),
                      )}
                    </Text>
                    <Text size="sm" ta="right" style={{ minWidth: 0 }} truncate>
                      {formatTraitValue(
                        traitItem[trait.accessorKey as string],
                        t("imagesModal.noValue"),
                        {
                          ...getTraitMeta(trait),
                          dateFormat,
                          dateTimeFormat,
                        },
                      )}
                    </Text>
                  </Group>
                ))}
              </>
            )}
          </Box>
        </Box>
      </Stack>
    </Dialog>
  );
};

export default ImageViewer;
